"use client";

import { useEffect, useId, useState } from "react";
import { getVariant } from "@/app/utilities/apis";
import { getOnRoadPrice, type CityData } from "@/utils/onRoadPrice";
import PromotedModelCard from "@/component/pdp/PromotedModelCard";
import { PROMOTED_MODEL_TRACKING } from "@/lib/constants/tracking";

interface PromotedModelsWidgetProps {
    models: any[]; // Filtered on the server so the cards are part of the SSR HTML.
    city?: CityData | null; // Page city; cards show on-road prices for it.
    isMobile?: boolean;
    className?: string; // Outer spacing, varies by placement.
}

export default function PromotedModelsWidget({ models, city, isMobile = false, className = "my-4 md:my-6" }: PromotedModelsWidgetProps) {
    const [onRoadPrices, setOnRoadPrices] = useState<Record<string, number | undefined>>({});
    const [activeIndex, setActiveIndex] = useState(0);
    const headingId = useId();

    useEffect(() => {
        if (!city || !models?.length) return;
        let mounted = true;
        Promise.all(models.map(async (car) => {
            const variantSlug = car.listing_summary?.overall?.cheapest?.variant_slug;
            const variant = variantSlug ? await getVariant(car.brand_slug, car.slug, variantSlug) : null;
            return [car._id, getOnRoadPrice(variant, city)?.onRoadPrice] as const;
        }))
            .then((entries) => { if (mounted) setOnRoadPrices(Object.fromEntries(entries)); })
            .catch(() => {});
        return () => { mounted = false; };
    }, [city, models]);

    if (!models?.length) return null;

    const handleScroll = ({ currentTarget: track }: React.UIEvent<HTMLDivElement>) => {
        const cardWidth = (track.firstElementChild as HTMLElement | null)?.offsetWidth;
        if (cardWidth) setActiveIndex(Math.min(models.length - 1, Math.round(track.scrollLeft / (cardWidth + 12))));
    };

    return (
        <section
            aria-labelledby={headingId}
            className={`${className} ${PROMOTED_MODEL_TRACKING.IMPRESSION} flex flex-col gap-3 lg:rounded-[16px] lg:border lg:border-[#E3E6EA] lg:bg-white lg:px-5 lg:py-4`}
            data-testid="promoted-models-widget"
        >
            <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1.5">
                <h2 id={headingId} className="text-[17px] font-bold leading-[22px] text-[#101828] lg:text-[18px]">
                    Offers You Shouldn&apos;t Miss
                </h2>
                <span className="rounded-[6px] bg-[#E8EBEF] px-[7px] py-[3px] text-[11px] font-medium uppercase tracking-[0.3px] text-[#4E555E] lg:bg-[#EEF0F3] lg:px-2 lg:py-1 lg:text-[12px]">
                    Promoted
                </span>
            </div>
            {models.length === 1 ? (
                <PromotedModelCard car={models[0]} position={1} isMobile={isMobile} onRoadPrice={onRoadPrices[models[0]._id]} cityName={city?.name} wide />
            ) : (
                <>
                    <div
                        onScroll={handleScroll}
                        className="-mx-4 flex snap-x snap-mandatory scroll-px-4 gap-3 overflow-x-auto px-4 scrollbar-hide lg:mx-0 lg:grid lg:grid-cols-[repeat(auto-fit,minmax(220px,1fr))] lg:overflow-visible lg:px-0"
                    >
                        {models.map((car, index) => (
                            <div key={car._id} className="max-w-[80vw] flex-[0_0_300px] snap-start lg:max-w-none">
                                <PromotedModelCard car={car} position={index + 1} isMobile={isMobile} onRoadPrice={onRoadPrices[car._id]} cityName={city?.name} />
                            </div>
                        ))}
                    </div>
                    <div className="flex justify-center gap-1.5 lg:hidden" aria-hidden="true">
                        {models.map((car, index) => (
                            <span key={car._id} className={`h-[6px] rounded-full transition-all ${index === activeIndex ? "w-[18px] bg-[#0B7A75]" : "w-[6px] bg-[#C6CCD2]"}`} />
                        ))}
                    </div>
                </>
            )}
        </section>
    );
}
